import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { authService } from '../api/services/auth.service'
import { AuthLayout } from '../components/layout/AuthLayout'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { PasswordInput } from '../components/ui/password-input'

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
})

const requestPasswordSchema = z.object({
  email: z.string().email('Enter a valid email'),
})

type LoginFormValues = z.infer<typeof loginSchema>
type RequestPasswordFormValues = z.infer<typeof requestPasswordSchema>

export function LoginPage() {
  const navigate = useNavigate()
  const [showRequestForm, setShowRequestForm] = useState(false)

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const requestForm = useForm<RequestPasswordFormValues>({
    resolver: zodResolver(requestPasswordSchema),
    defaultValues: { email: '' },
  })

  const onLogin = loginForm.handleSubmit(async (values) => {
    try {
      await authService.login(values.email, values.password)
      navigate('/verify-otp', { state: { email: values.email } })
    } catch {
      // API errors are toasted globally by the axios interceptor.
    }
  })

  const onRequestPassword = requestForm.handleSubmit(async (values) => {
    try {
      await authService.requestAdminPasswordReset(values.email)
    } catch {
      // API errors are toasted globally by the axios interceptor.
    }
  })

  if (showRequestForm) {
    return (
      <AuthLayout
        title="Request password reset"
        description="Enter your email. An administrator will review your request and set a new password."
        footer={
          <button
            type="button"
            className="text-sm font-semibold text-green-600 hover:text-green-700"
            onClick={() => setShowRequestForm(false)}
          >
            Back to Login
          </button>
        }
      >
        <form className="space-y-4" onSubmit={onRequestPassword}>
          <FormRequiredLegend />
          <div>
            <FieldLabel required>Email</FieldLabel>
            <Input className="mt-1" type="email" autoComplete="email" {...requestForm.register('email')} />
            {requestForm.formState.errors.email && (
              <p className="mt-1 text-xs text-danger">
                {requestForm.formState.errors.email.message}
              </p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={requestForm.formState.isSubmitting}>
            {requestForm.formState.isSubmitting ? 'Submitting...' : 'Request Reset'}
          </Button>
        </form>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Welcome back"
      description="Sign in with your email, then verify the code we send to your email."
      footer={
        <p className="text-center text-sm text-ink-600">
          <button
            type="button"
            className="font-semibold text-green-600 hover:text-green-700"
            onClick={() => setShowRequestForm(true)}
          >
            Forgot Password?
          </button>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={onLogin} autoComplete="off">
        <FormRequiredLegend />
        <div>
          <FieldLabel required htmlFor="login-email">
            Email
          </FieldLabel>
          <Input
            id="login-email"
            className="mt-1"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            {...loginForm.register('email')}
          />
          {loginForm.formState.errors.email && (
            <p className="mt-1 text-xs text-danger">{loginForm.formState.errors.email.message}</p>
          )}
        </div>
        <div>
          <FieldLabel required htmlFor="login-password">
            Password
          </FieldLabel>
          <PasswordInput
            id="login-password"
            wrapperClassName="mt-1"
            autoComplete="current-password"
            {...loginForm.register('password')}
          />
          {loginForm.formState.errors.password && (
            <p className="mt-1 text-xs text-danger">
              {loginForm.formState.errors.password.message}
            </p>
          )}
        </div>
        <Button type="submit" className="w-full" disabled={loginForm.formState.isSubmitting}>
          {loginForm.formState.isSubmitting ? 'Sending OTP...' : 'Continue'}
        </Button>
        <Link to="/doctor/signup" className="block">
          <Button type="button" variant="secondary" className="w-full">
            Sign Up
          </Button>
        </Link>
      </form>
    </AuthLayout>
  )
}
